import React, { useState } from 'react';
import { Box, Typography, Avatar, Chip, IconButton, Tooltip } from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import ReactMarkdown from 'react-markdown';

// Render user message content with @AgentName highlighted as chips
function UserContent({ content, compact = false }) {
  const parts = content.split(/(@\S+)/g);
  return (
    <Typography sx={{ fontSize: compact ? 13 : 14, whiteSpace: 'pre-wrap' }}>
      {parts.map((part, i) =>
        part.startsWith('@') ? (
          <Chip
            key={i}
            label={part}
            size="small"
            icon={<SmartToyIcon sx={{ fontSize: '14px !important' }} />}
            sx={{
              height: 22,
              fontSize: 12,
              fontWeight: 600,
              bgcolor: 'rgba(255,255,255,0.2)',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.3)',
              mx: 0.3,
              verticalAlign: 'middle',
              '& .MuiChip-icon': { color: '#fff' },
            }}
          />
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        )
      )}
    </Typography>
  );
}

export default function ChatMessage({ message, agentName, agentIcon, compact = false }) {
  const isUser = message.role === 'user';
  const isStreaming = message.isStreaming;
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        gap: compact ? 1 : 1.5,
        mb: compact ? 1.5 : 2.5,
        flexDirection: isUser ? 'row-reverse' : 'row',
        alignItems: 'flex-start',
        '& .copy-btn': { opacity: 0 },
        '&:hover .copy-btn': { opacity: compact ? 0 : 1 },
      }}
    >
      <Avatar
        sx={{
          width: compact ? 24 : 32,
          height: compact ? 24 : 32,
          bgcolor: isUser ? '#526075' : '#f1f4f5',
          color: isUser ? '#fff' : '#767c7e',
          fontSize: compact ? 12 : 14,
          flexShrink: 0,
        }}
      >
        {isUser ? <PersonIcon sx={{ fontSize: compact ? 14 : 18 }} /> : <SmartToyIcon sx={{ fontSize: compact ? 14 : 18 }} />}
      </Avatar>
      <Box
        sx={{
          maxWidth: compact ? '90%' : '75%',
          bgcolor: isUser ? '#526075' : '#fff',
          color: isUser ? '#fff' : '#2d3335',
          borderRadius: isUser ? '20px 20px 6px 20px' : '20px 20px 20px 6px',
          px: compact ? 1.5 : 2,
          py: compact ? 1 : 1.5,
          boxShadow: '0 1px 3px rgba(45,51,53,0.06)',
          border: isUser ? 'none' : '1px solid rgba(45,51,53,0.08)',
          position: 'relative',
          '& p': { m: 0, lineHeight: 1.6 },
          '& p + p': { mt: 1 },
          '& pre': {
            bgcolor: isUser ? 'rgba(0,0,0,0.15)' : '#f1f4f5',
            borderRadius: 1,
            p: 1.5,
            overflow: 'auto',
            fontSize: 13,
          },
          '& code': {
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: 13,
          },
          '& ul, & ol': { pl: 2.5, my: 0.5 },
          '& li': { mb: 0.3 },
        }}
      >
        {!isUser && agentName && (
          <Typography variant="caption" sx={{ color: '#526075', fontWeight: 600, display: 'block', mb: 0.5 }}>
            {agentName}
          </Typography>
        )}
        {isUser ? (
          <UserContent content={message.content} compact={compact} />
        ) : (
          <Box sx={{ fontSize: compact ? 13 : 14 }}>
            <ReactMarkdown>{message.content || ''}</ReactMarkdown>
            {isStreaming && (
              <Box component="span" sx={{
                display: 'inline-block',
                width: 6,
                height: 16,
                bgcolor: '#526075',
                ml: 0.5,
                animation: 'blink 1s infinite',
                '@keyframes blink': {
                  '0%, 50%': { opacity: 1 },
                  '51%, 100%': { opacity: 0 },
                },
              }} />
            )}
          </Box>
        )}
        {!compact && !isUser && !isStreaming && message.content && (
          <Tooltip title={copied ? 'Gekopieerd!' : 'Kopieer tekst'} arrow>
            <IconButton
              className="copy-btn"
              size="small"
              onClick={handleCopy}
              sx={{
                position: 'absolute',
                bottom: 4,
                right: 4,
                p: 0.5,
                color: copied ? '#10b981' : '#adb3b5',
                bgcolor: 'rgba(255,255,255,0.9)',
                border: '1px solid rgba(45,51,53,0.08)',
                borderRadius: '50%',
                transition: 'opacity 0.15s, color 0.15s',
                '&:hover': { bgcolor: '#f1f4f5', color: '#526075' },
              }}
            >
              {copied ? <CheckIcon sx={{ fontSize: 14 }} /> : <ContentCopyIcon sx={{ fontSize: 14 }} />}
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}
